import React, { useEffect, useRef } from 'react';

/**
 * Confirm step for an unusually long catch (#85). Not window.confirm: an
 * accessible alert dialog with focus on "Fix length" (the safe choice), and
 * Escape also means "Fix length". Shared by the member and guest catch forms.
 */
interface LengthConfirmDialogProps {
  open: boolean;
  lengthCm: number;
  speciesLabel: string;
  onFixLength: () => void;
  onConfirm: () => void;
}

const LengthConfirmDialog: React.FC<LengthConfirmDialogProps> = ({ open, lengthCm, speciesLabel, onFixLength, onConfirm }) => {
  const fixRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) fixRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="length-confirm-title"
        aria-describedby="length-confirm-desc"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onFixLength();
        }}
        className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl"
      >
        <h2 id="length-confirm-title" className="text-lg font-bold text-gray-900">
          Check the length
        </h2>
        <p id="length-confirm-desc" className="mt-2 text-gray-700">
          {lengthCm}cm is very big for a {speciesLabel}. Is that right?
        </p>
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onConfirm}
            className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-fishing-blue"
          >
            Yes, submit
          </button>
          <button
            ref={fixRef}
            type="button"
            onClick={onFixLength}
            className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-fishing-blue"
          >
            Fix length
          </button>
        </div>
      </div>
    </div>
  );
};

export default LengthConfirmDialog;
